import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "../client";
import type { Patron, PatronCategory, CreatePatronInput, UpdatePatronInput } from "../types";

const PATRONS_KEY = "patrons";
const CATEGORIES_KEY = "patron-categories";

/**
 * Search patrons.
 */
export function usePatronsSearch(query?: string, status?: string) {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (status) params.set("status", status);
  const queryString = params.toString();

  return useQuery({
    queryKey: [PATRONS_KEY, "search", query, status],
    queryFn: async () => {
      const url = queryString ? `/circulation/patrons?${queryString}` : "/circulation/patrons";
      return get<Patron[]>(url);
    },
  });
}

/**
 * Get a single patron by ID.
 */
export function usePatron(id: string | undefined) {
  return useQuery({
    queryKey: [PATRONS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Patron>(`/circulation/patrons/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Get patron by barcode.
 */
export function usePatronByBarcode(barcode: string | undefined) {
  return useQuery({
    queryKey: [PATRONS_KEY, "barcode", barcode],
    queryFn: async () => {
      if (!barcode) throw new Error("Barcode is required");
      const patrons = await get<Patron[]>(`/circulation/patrons?q=${encodeURIComponent(barcode)}`);
      // Find exact barcode match
      return patrons.find((p) => p.barcode === barcode) ?? null;
    },
    enabled: !!barcode,
  });
}

/**
 * Create a new patron.
 */
export function useCreatePatron() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePatronInput) => {
      return post<Patron>("/circulation/patrons", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [PATRONS_KEY] });
    },
  });
}

/**
 * Update a patron.
 */
export function useUpdatePatron(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdatePatronInput) => {
      return patch<Patron>(`/circulation/patrons/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [PATRONS_KEY] });
    },
  });
}

/**
 * Delete a patron.
 */
export function useDeletePatron() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/circulation/patrons/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [PATRONS_KEY] });
    },
  });
}

// Patron categories

/**
 * Email the patron the link to activate their portal account (or reset their password).
 */
export function useSendPortalInvitation(id: string) {
  return useMutation({
    mutationFn: () => post<{ message: string }>(`/circulation/patrons/${id}/portal-invitation`),
  });
}

/**
 * Get all patron categories.
 */
export function usePatronCategories() {
  return useQuery({
    queryKey: [CATEGORIES_KEY],
    queryFn: async () => {
      return get<PatronCategory[]>("/circulation/patron-categories");
    },
  });
}

/**
 * Get a single patron category by ID.
 */
export function usePatronCategory(id: string | undefined) {
  return useQuery({
    queryKey: [CATEGORIES_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<PatronCategory>(`/circulation/patron-categories/${id}`);
    },
    enabled: !!id,
  });
}
